import { Col, Row } from 'antd';
import { SubmissionErrors } from 'final-form';
import React from 'react';
import { Field, Form } from 'react-final-form';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { ScaleType, ScaleVisualization } from '~src/interfaces/scale.interface';
import { ButtonWrapper } from '~src/modules/attributes/components/add/attributeAddForm.sc';
import { NewScaleSubmitData } from '~src/modules/scales/actions/scaleActions';
import { scaleTypes } from '~src/modules/scales/constants/scaleTypes';
import { StoreI } from '~src/rootReducer';
import { routes } from '~src/routingConfig';
import { getCompanyContentLocale } from '~src/services/session';
import { validatorMaxLength40, validatorRequired } from '~src/services/validators';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import CardsRadio from '~src/shared/form/cardsRadio/CardsRadio';
import CustomFormInput from '~src/shared/form/inputs/FormInput';
import { composeValidators } from '~src/shared/reactFinalForm/validators';
import { FieldType } from '~src/types';

import { CreateScaleBtn, StyledForm } from './scaleAddForm.sc';

interface OwnProps extends Pick<RouteComponentProps<any>, 'history'> {
  submitFrm(scale: NewScaleSubmitData): Promise<SubmissionErrors | void>;
}

interface StateProps {
  contentLocale: string;
  loadingAdd: boolean;
}

type Props = OwnProps & StateProps & WithTranslation;

interface FormValues {
  name: string;
  visualization: ScaleVisualization;
}

class ScaleAddForm extends React.Component<Props> {
  mapValuesBeforeSubmit = (values: FormValues) => {
    const submitValues = {
      ...values,
      name: { [this.props.contentLocale]: values.name.trim() },
      type: values.visualization === ScaleVisualization.McqOptions ? ScaleType.Categorial : ScaleType.Ordinal,
    };
    return this.props.submitFrm(submitValues);
  };

  goBack = () => {
    this.props.history.push(routes.contentScales.scales);
  };

  renderField({ input, label, meta: { touched, error }, labelKey }: FieldType) {
    return (
      <CustomFormInput
        {...input}
        label={label}
        type={input.type}
        error={touched ? error : undefined}
        labelKey={labelKey}
      />
    );
  }

  render() {
    const { t, loadingAdd } = this.props;

    return (
      <Form initialValues={{ visualization: ScaleVisualization.Stars }} onSubmit={this.mapValuesBeforeSubmit}>
        {({ handleSubmit, values }) => (
          <StyledForm onSubmit={handleSubmit} noValidate>
            <Row gutter={[16, 16]} wrap justify={'center'}>
              {scaleTypes.map((item, index) => (
                <Col
                  xs={{ flex: '100%' }}
                  sm={{ flex: '50%' }}
                  md={{ flex: '40%' }}
                  lg={{ flex: '20%' }}
                  xl={{ flex: '10%' }}
                  key={item.value}
                >
                  <label htmlFor={`visualization_${index}`}>
                    <CardsRadio
                      t={t}
                      itemTypeSelected={values.visualization}
                      item={item}
                      index={index}
                      fieldName="visualization"
                    />
                  </label>
                </Col>
              ))}
            </Row>
            <Row>
              <Col style={{ marginTop: '60px', width: '100%' }}>
                <Field
                  name="name"
                  label={t('x.scales.add.input.placeholder.scale_name')}
                  type="text"
                  children={this.renderField}
                  validate={composeValidators([validatorRequired, validatorMaxLength40])}
                  labelKey="x.scales.add.input.placeholder.scale_name"
                />
              </Col>
            </Row>
            <ButtonWrapper>
              <SecondaryButton type="button" onClick={this.goBack}>
                <span data-translation="x.scales.add.button.cancel">{t('x.scales.add.button.cancel')}</span>
              </SecondaryButton>
              <CreateScaleBtn isLoading={loadingAdd}>
                <span data-translation="x.scales.add.button.create">{t('x.scales.add.button.create')}</span>
              </CreateScaleBtn>
            </ButtonWrapper>
          </StyledForm>
        )}
      </Form>
    );
  }
}

const mapStateToProps = (state: StoreI): StateProps => ({
  contentLocale: getCompanyContentLocale(state),
  loadingAdd: state.scales.loadingAdd,
});

const ScaleAddFormT = withTranslation()(ScaleAddForm);
const ScaleAddFormC = connect<StateProps, {}, OwnProps, StoreI>(mapStateToProps)(ScaleAddFormT);

export default ScaleAddFormC;
